import React, { useEffect, useState } from "react";
import { Download, X, Sparkles, Share, Plus } from "lucide-react";
import { useI18n } from "../lib/i18n";

/**
 * Smart install prompt for PWA. Ensures CARTAL is easy to add to the home
 * screen on every browser we care about:
 *  - Chrome / Edge / Android → `beforeinstallprompt` (native prompt)
 *  - iOS Safari → visual, step-by-step overlay (iOS never fires the event)
 *  - iPadOS (masquerades as Mac) → detected via `maxTouchPoints`
 *  - Desktop Safari → same iOS-style visual overlay
 *
 * Session-scoped dismissal: if the user closes the prompt, it stays hidden
 * for the rest of the current tab session but reappears on the NEXT visit
 * (per product owner's request: "every entry until installed").
 * `sessionStorage` is cleared when the tab closes, so the next launch shows
 * the prompt again. Once installed, `isStandalone()` returns true and the
 * banner is silent forever.
 */
const DISMISS_KEY = "cartal_pwa_install_dismissed_session";

function shouldHide() {
  if (typeof window === "undefined") return true;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIosLike() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";
  // Classic iPhone/iPad/iPod detection
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // Modern iPadOS reports as Mac + touch — sniff via maxTouchPoints
  if (/Mac/.test(platform) && (window.navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

function isSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|Edg|OPR/.test(ua);
}

export default function PWAInstallPrompt() {
  const { lang } = useI18n();
  const [state, setState] = useState({
    deferred: null,
    show: false,
    variant: null, // "native" | "ios" | null
  });

  useEffect(() => {
    if (isStandalone() || shouldHide()) return;

    // ── Native prompt (Chrome/Edge/Android/desktop Chromium) ───────────
    const nativeHandler = (e) => {
      e.preventDefault();
      setState({ deferred: e, show: true, variant: "native" });
    };
    window.addEventListener("beforeinstallprompt", nativeHandler);

    // Reset dismissal flag if user actually installs
    const installedHandler = () => {
      try {
        window.sessionStorage.removeItem(DISMISS_KEY);
        // Also clear the legacy localStorage key from earlier versions
        window.localStorage.removeItem("cartal_pwa_install_dismissed_at");
      } catch { /* noop */ }
      setState((s) => ({ ...s, show: false }));
    };
    window.addEventListener("appinstalled", installedHandler);

    // ── Fallback for browsers that never fire beforeinstallprompt ──────
    // (Safari on iPhone/iPad/macOS, Firefox on iOS, in-app browsers…)
    // Show a visual "Add to Home Screen" overlay after a short delay so
    // we don't collide with the native prompt if it eventually fires.
    let fallbackTimer;
    if (isIosLike() || isSafari()) {
      fallbackTimer = setTimeout(() => {
        setState((s) => (s.show ? s : { deferred: null, show: true, variant: "ios" }));
      }, 3500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", nativeHandler);
      window.removeEventListener("appinstalled", installedHandler);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  if (!state.show) return null;

  const dismiss = () => {
    // Session-scoped only: hides for this tab, reappears on next visit.
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setState((s) => ({ ...s, show: false }));
  };

  const install = async () => {
    if (state.deferred) {
      state.deferred.prompt();
      const choice = await state.deferred.userChoice;
      if (choice.outcome === "dismissed") dismiss();
      setState({ deferred: null, show: false, variant: null });
    }
  };

  // ── iOS / Safari visual overlay ────────────────────────────────────────
  if (state.variant === "ios") {
    return (
      <div
        className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:end-4 sm:bottom-4 sm:w-[420px] z-[80] rounded-3xl shadow-2xl overflow-hidden pwa-prompt-glow"
        style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#131313 60%,#1c1712 100%)" }}
        role="dialog"
        aria-live="polite"
        data-testid="pwa-install-prompt-ios"
      >
        {/* Golden accent bar */}
        <div
          className="h-1"
          style={{ background: "linear-gradient(90deg,#8a6f2e,#d4b56e,#f5d478,#d4b56e,#8a6f2e)", backgroundSize: "200% 100%" }}
        />

        <div className="px-4 py-4 sm:px-5 sm:py-5 text-[#f1ecdf]">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex-shrink-0 flex items-center justify-center shadow-lg"
              style={{ background: "linear-gradient(135deg,#f5d478,#c9a55c,#8a6f2e)" }}
            >
              <Sparkles className="w-5 h-5 text-[#0a0a0a]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-semibold leading-tight">
                {lang === "ar" ? "ثبّت كارتال على شاشتك" : "Install CARTAL on your device"}
              </div>
              <p className="text-[12px] text-[#f1ecdf]/75 mt-1 leading-snug">
                {lang === "ar"
                  ? "تجربة أسرع وعمل بدون إنترنت وإشعارات فورية — بلا متجر تطبيقات."
                  : "Faster, works offline, instant notifications — no App Store."}
              </p>
            </div>
            <button
              onClick={dismiss}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#f1ecdf]/60 hover:text-[#f1ecdf] hover:bg-[#f1ecdf]/10 transition-colors"
              aria-label="Dismiss"
              data-testid="pwa-dismiss-ios"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* iOS step-by-step */}
          <div className="mt-4 rounded-2xl p-3.5 space-y-2.5 border border-[#c9a55c]/20 bg-[#f1ecdf]/5">
            <div className="flex items-center gap-3 text-[13px]">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#c9a55c] text-[#0a0a0a] font-bold text-[11px] flex-shrink-0">1</span>
              <span className="flex-1">
                {lang === "ar" ? "اضغط زر" : "Tap the"}{" "}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#f1ecdf]/10 text-[#f5d478] font-semibold" dir="ltr">
                  <Share className="w-3.5 h-3.5" />
                  {lang === "ar" ? "المشاركة" : "Share"}
                </span>{" "}
                {lang === "ar" ? "في المتصفح" : "button in your browser"}
              </span>
            </div>
            <div className="flex items-center gap-3 text-[13px]">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#c9a55c] text-[#0a0a0a] font-bold text-[11px] flex-shrink-0">2</span>
              <span className="flex-1">
                {lang === "ar" ? "اختر" : "Choose"}{" "}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#f1ecdf]/10 text-[#f5d478] font-semibold">
                  <Plus className="w-3.5 h-3.5" />
                  {lang === "ar" ? "إضافة إلى الشاشة الرئيسية" : "Add to Home Screen"}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-3 text-[13px]">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#c9a55c] text-[#0a0a0a] font-bold text-[11px] flex-shrink-0">3</span>
              <span className="flex-1 text-[#f1ecdf]/90">
                {lang === "ar" ? "اضغط «إضافة» في الأعلى — انتهى!" : "Tap 'Add' in the corner — done!"}
              </span>
            </div>
          </div>

          <button
            onClick={dismiss}
            className="mt-4 w-full text-[12px] text-[#f1ecdf]/60 hover:text-[#f1ecdf]/90 py-1.5"
            data-testid="pwa-dismiss-ios-cta"
          >
            {lang === "ar" ? "لاحقاً" : "Maybe later"}
          </button>
        </div>
      </div>
    );
  }

  // ── Native install banner (Chrome / Edge / Android / desktop Chromium) ─
  return (
    <div
      className="fixed inset-x-3 bottom-3 sm:left-1/2 sm:-translate-x-1/2 sm:inset-x-auto sm:bottom-4 sm:max-w-md z-[80] rounded-2xl shadow-2xl overflow-hidden pwa-prompt-glow"
      style={{ background: "#0a0a0a" }}
      data-testid="pwa-install-prompt"
    >
      {/* Golden accent bar */}
      <div
        className="h-1"
        style={{ background: "linear-gradient(90deg,#8a6f2e,#d4b56e,#f5d478,#d4b56e,#8a6f2e)", backgroundSize: "200% 100%" }}
      />
      <div className="flex items-stretch text-[#f1ecdf]">
        <div className="w-14 flex-shrink-0 flex items-center justify-center" style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)" }}>
          <Sparkles className="w-6 h-6 text-[#0a0a0a]" strokeWidth={2.5} />
        </div>
        <div className="flex-1 px-4 py-3 min-w-0">
          <div className="font-display text-base font-semibold leading-tight">
            {lang === "ar" ? "ثبّت كارتال على شاشتك" : "Install CARTAL"}
          </div>
          <p className="text-[12px] text-[#f1ecdf]/75 mt-0.5 leading-snug line-clamp-2">
            {lang === "ar"
              ? "تجربة أسرع، عمل بدون إنترنت، إشعارات فورية."
              : "Faster experience, offline support, instant notifications."}
          </p>
        </div>
        <div className="flex flex-col gap-1 p-2 flex-shrink-0 justify-center">
          <button
            onClick={install}
            className="inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-[12px] font-bold hover:scale-[1.03] transition-transform shadow-md whitespace-nowrap"
            style={{ background: "linear-gradient(135deg,#f5d478,#c9a55c)", color: "#0a0a0a" }}
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" />
            {lang === "ar" ? "تثبيت" : "Install"}
          </button>
          <button
            onClick={dismiss}
            className="inline-flex items-center justify-center px-2 py-1 rounded-lg text-[#f1ecdf]/70 hover:text-[#f1ecdf] hover:bg-[#f1ecdf]/10 text-[11px]"
            data-testid="pwa-dismiss"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
